'use client';

import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { scrollToElement } from './utils/scroll-positioning';

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface SectionNavigationProps {
  headings: Heading[];
  currentSection: string;
}

export function SectionNavigation({ headings, currentSection }: SectionNavigationProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousSection, setPreviousSection] = useState<Heading | null>(null);
  const [nextSection, setNextSection] = useState<Heading | null>(null);
  
  // Only work with H2 headings
  const h2Headings = headings.filter(h => h.level === 2);
  
  useEffect(() => {
    // Find current section index
    const index = h2Headings.findIndex(h => h.text === currentSection);
    if (index !== -1) {
      setCurrentIndex(index);
      setPreviousSection(index > 0 ? h2Headings[index - 1] : null);
      setNextSection(index < h2Headings.length - 1 ? h2Headings[index + 1] : null);
    }
  }, [currentSection, h2Headings]);
  
  const scrollToSection = async (id: string) => {
    try {
      const result = await scrollToElement({
        targetElementId: id,
        forceScrollBehavior: 'smooth',
        debugMode: false,
      });
      
      if (!result.success) {
        console.warn(`[SectionNavigation] Failed to scroll to section: ${id}`);
      }
    } catch (error) {
      console.error(`[SectionNavigation] Error scrolling to section ${id}:`, error);
      // Fallback to old method
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };
  
  // Don't render if no navigation available
  if (!previousSection && !nextSection) {
    return null;
  }
  
  return (
    <>
      {/* Desktop Version - Fixed Bottom */}
      <div className="section-navigation-bottom hidden lg:block fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 flex items-center justify-between">
            {/* Previous Section */}
            <div className="flex-1">
              {previousSection ? (
                <button
                  onClick={() => scrollToSection(previousSection.id)}
                  className="group flex items-center space-x-2 text-gray-600 hover:text-primary transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  <div className="text-left">
                    <div className="text-xs text-gray-500">Previous</div>
                    <div className="text-sm font-medium truncate max-w-xs">
                      {previousSection.text}
                    </div>
                  </div>
                </button>
              ) : (
                <div />
              )}
            </div>
            
            {/* Progress Indicator */}
            <div className="px-4">
              <div className="text-sm text-gray-500">
                Section {currentIndex + 1} of {h2Headings.length}
              </div>
            </div>
            
            {/* Next Section */}
            <div className="flex-1 flex justify-end">
              {nextSection ? (
                <button
                  onClick={() => scrollToSection(nextSection.id)}
                  className="group flex items-center space-x-2 text-gray-600 hover:text-primary transition-colors"
                >
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Next</div>
                    <div className="text-sm font-medium truncate max-w-xs">
                      {nextSection.text}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Version - Inline */}
      <div className="lg:hidden mt-12 pt-8 border-t border-gray-200">
        <div className="space-y-3">
          {/* Progress */}
          <div className="text-center text-sm text-gray-500 mb-4">
            Section {currentIndex + 1} of {h2Headings.length}
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {previousSection ? (
              <button
                onClick={() => scrollToSection(previousSection.id)}
                className="flex-1 group flex items-center justify-center space-x-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <div className="text-left">
                  <div className="text-xs text-gray-500">Previous</div>
                  <div className="text-sm font-medium">
                    {previousSection.text}
                  </div>
                </div>
              </button>
            ) : (
              <div className="flex-1" />
            )}
            
            {nextSection ? (
              <button
                onClick={() => scrollToSection(nextSection.id)}
                className="flex-1 group flex items-center justify-center space-x-2 px-4 py-3 bg-primary text-white hover:bg-primary/90 rounded-lg transition-colors"
              >
                <div className="text-right">
                  <div className="text-xs opacity-90">Next</div>
                  <div className="text-sm font-medium">
                    {nextSection.text}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            ) : (
              <div className="flex-1" />
            )}
          </div>
        </div>
      </div>
    </>
  );
}